import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { useAuth } from "../context/AuthContext";
import { CheckCircle2, AlertCircle, Loader2, Mail, KeyRound, Eye, EyeOff, RefreshCw, Send } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Public landing page reached via the secure invite link emailed by the coach.
 *
 *  ─ Mounts on `/invite/:token`
 *  ─ Validates the token against `GET /api/invites/:token`
 *      • 200 → show password setup form, prefill name + email
 *      • 404/410/409 → show friendly explanation + "ask for new invite" CTA
 *  ─ Submits `POST /api/invites/:token/accept` to set password
 *      and create the pending user account
 *
 * Hitting this endpoint successfully also marks the athlete's
 * `_invitation.email_verified_at` server-side — clicking the unique link IS
 * the email-ownership proof.
 */
export default function AthleteInvite() {
  const { token } = useParams();
  const navigate = useNavigate();
  const { refreshUser } = useAuth();
  const [phase, setPhase] = useState("loading"); // loading | ready | done | error
  const [error, setError] = useState("");
  const [errorCode, setErrorCode] = useState(0);
  const [invite, setInvite] = useState(null);
  const [name, setName] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [show, setShow] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [acceptStatus, setAcceptStatus] = useState({ ok: true, error: null });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/invites/${token}`);
        if (cancelled) return;
        setInvite(data);
        setName(data.athlete_name || "");
        setPhase("ready");
      } catch (e) {
        if (cancelled) return;
        const detail = e?.response?.data?.detail || "This invitation link is invalid or has expired.";
        setError(detail);
        setErrorCode(e?.response?.status || 0);
        setPhase("error");
      }
    })();
    return () => { cancelled = true; };
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (password.length < 8) {
      setError("Password must be at least 8 characters.");
      return;
    }
    if (password !== confirm) {
      setError("Passwords don't match.");
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await axios.post(`${API}/invites/${token}/accept`, { password, name });
      setAcceptStatus(data?.email_status || { ok: true, error: null });
      // NEW: the backend now returns an auth token so the athlete drops
      // straight into their dashboard — no manual "log in with the password
      // you just chose" step. Falls back gracefully if the token is missing.
      if (data?.token) {
        localStorage.setItem("token", data.token);
        try { await refreshUser(); } catch { /* non-fatal */ }
      }
      setPhase("done");
    } catch (e2) {
      setError(e2?.response?.data?.detail || "Couldn't complete registration. Try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (phase === "loading") {
    return (
      <Shell>
        <div className="text-center" data-testid="invite-loading">
          <Loader2 className="w-10 h-10 text-[#A78BFA] mx-auto mb-4 animate-spin" />
          <div className="overline text-[#A78BFA]">// verifying invitation</div>
        </div>
      </Shell>
    );
  }

  if (phase === "error") {
    // 409 = already used (they finished registration before) — nudge them to
    // /login rather than requesting yet another invite. 410 / 404 = expired
    // or broken → offer the self-serve resend form.
    const alreadyUsed = errorCode === 409;
    return (
      <Shell>
        <div className="text-center max-w-md w-full" data-testid="invite-error-screen">
          <AlertCircle className="w-16 h-16 text-[#FF3B30] mx-auto mb-6" />
          <div className="overline text-[#FF3B30] mb-2">// invitation problem</div>
          <h2 className="font-heading text-3xl font-black uppercase mb-4">
            {alreadyUsed ? "Already Used" : "Link Not Valid"}
          </h2>
          <p className="text-[#A1A1AA] text-sm mb-6">{error}</p>
          {alreadyUsed ? (
            <Link
              to="/login"
              data-testid="invite-error-login-link"
              className="inline-block px-6 py-3 border border-white/20 hover:border-white/40 font-heading text-xs uppercase tracking-[0.25em]"
            >
              Go to Login
            </Link>
          ) : (
            <RequestNewInviteForm />
          )}
        </div>
      </Shell>
    );
  }

  if (phase === "done") {
    // At this point the athlete is signed in (auth token was accepted above).
    // Give them a single-tap "Continue" that lands on the dashboard — where
    // the Install App banner they were promised is waiting.
    return (
      <Shell>
        <div className="text-center max-w-md" data-testid="invite-done-screen">
          <CheckCircle2 className="w-16 h-16 text-[#34C759] mx-auto mb-6" />
          <div className="overline text-[#34C759] mb-2">// account activated</div>
          <h2 className="font-heading text-3xl font-black uppercase mb-4">You're In!</h2>
          <p className="text-[#A1A1AA] text-sm mb-6">
            Welcome to <b className="text-white">RD SmartMetrics</b>,{" "}
            <span className="text-white font-bold">{name}</span> — your account is active and you're
            signed in.
          </p>
          {!acceptStatus.ok && acceptStatus.error && (
            <div
              className="border border-[#FF3B30]/30 bg-[#FF3B30]/5 rounded-lg p-3 mb-6 text-left"
              data-testid="invite-email-failed-banner"
            >
              <div className="overline text-[#FF3B30] mb-1 text-[10px]">
                // welcome email failed
              </div>
              <p className="text-[11px] text-[#FCA5A5]">
                We couldn't send your welcome email, but your account is fully active. You can
                continue to your dashboard.
              </p>
            </div>
          )}
          <button
            onClick={() => navigate("/")}
            data-testid="invite-done-continue-btn"
            className="inline-block px-6 py-3 bg-[#7C3AED] hover:bg-[#6D28D9] text-white font-heading text-xs uppercase tracking-[0.25em]"
          >
            Continue to Dashboard
          </button>
        </div>
      </Shell>
    );
  }

  // phase === "ready"
  return (
    <Shell>
      <div className="w-full max-w-md" data-testid="invite-ready-screen">
        <div className="overline text-[#A78BFA] mb-2">// secure invitation</div>
        <h2 className="font-heading text-4xl font-black uppercase mb-2">Set Your Password</h2>
        <p className="text-[#A1A1AA] text-sm mb-6">
          Welcome to <b className="text-white">RD SmartMetrics</b>. Your email
          <span className="text-[#A78BFA] mx-1">{invite.email}</span>
          is verified — set a password to finish creating your account.
        </p>

        <form onSubmit={submit} className="space-y-4" data-testid="invite-form">
          <Field label="Full Name" icon={Mail}>
            <input
              type="text" value={name} onChange={(e) => setName(e.target.value)}
              required data-testid="invite-name-input"
              className="w-full bg-transparent border-b border-white/20 focus:border-[#A78BFA] outline-none py-2 text-base"
            />
          </Field>

          <Field label="Email" icon={Mail}>
            <input
              type="email" value={invite.email} disabled data-testid="invite-email-input"
              className="w-full bg-transparent border-b border-white/10 outline-none py-2 text-base text-[#71717A] cursor-not-allowed"
            />
          </Field>

          <Field label="Password (min 8 chars)" icon={KeyRound}>
            <div className="relative">
              <input
                type={show ? "text" : "password"} value={password}
                onChange={(e) => setPassword(e.target.value)}
                required minLength={8} data-testid="invite-password-input"
                className="w-full bg-transparent border-b border-white/20 focus:border-[#A78BFA] outline-none py-2 pr-9 text-base"
              />
              <button type="button" onClick={() => setShow((v) => !v)}
                data-testid="invite-password-show-toggle"
                className="absolute right-0 top-2 text-[#71717A] hover:text-white">
                {show ? <EyeOff size={16} /> : <Eye size={16} />}
              </button>
            </div>
          </Field>

          <Field label="Confirm Password" icon={KeyRound}>
            <input
              type={show ? "text" : "password"} value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              required minLength={8} data-testid="invite-password-confirm"
              className="w-full bg-transparent border-b border-white/20 focus:border-[#A78BFA] outline-none py-2 text-base"
            />
          </Field>

          {error && <div className="text-[#FF3B30] text-xs" data-testid="invite-error">{error}</div>}

          <button type="submit" disabled={submitting} data-testid="invite-submit-btn"
            className="w-full py-3 bg-[#FF3B30] hover:bg-[#E0342B] disabled:opacity-50 disabled:cursor-not-allowed text-white font-heading text-xs uppercase tracking-[0.25em] flex items-center justify-center gap-2">
            {submitting && <Loader2 size={14} className="animate-spin" />}
            {submitting ? "Creating Account…" : "Complete Registration"}
          </button>

          <p className="text-[10px] text-[#71717A] text-center leading-relaxed pt-2">
            After you set your password, your account activates and you go straight to your dashboard.
          </p>
        </form>
      </div>
    </Shell>
  );
}

function Shell({ children }) {
  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white flex items-center justify-center p-6">
      {children}
    </div>
  );
}

/**
 * Self-serve resend flow shown when a link is broken / expired.
 * POST /api/invites/request-new is rate-limited (3/hr per email) and always
 * returns a generic acknowledgement to avoid email-enumeration attacks, so
 * we show the same soft "if that email is on file…" message on success
 * regardless of whether the address actually matched a record.
 */
function RequestNewInviteForm() {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const [err, setErr] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (!email || !email.includes("@")) {
      setErr("Enter the email address your coach used to invite you.");
      return;
    }
    setBusy(true);
    try {
      await axios.post(`${API}/invites/request-new`, { email: email.trim().toLowerCase() });
      setSent(true);
    } catch (e2) {
      // 429 rate-limit gets a clear retry message; everything else falls back
      // to a generic apology so we don't leak whether the address is valid.
      if (e2?.response?.status === 429) {
        setErr(e2.response.data?.detail || "Too many requests. Try again later.");
      } else {
        setErr("Couldn't process that request right now. Please try again in a minute.");
      }
    } finally {
      setBusy(false);
    }
  };

  if (sent) {
    return (
      <div
        className="border border-[#34C759]/40 bg-[#34C759]/10 rounded-lg p-4 text-left"
        data-testid="invite-resend-success"
      >
        <div className="flex items-center gap-2 mb-2 text-[#34C759]">
          <CheckCircle2 size={16} />
          <span className="overline text-[10px]">// invitation sent</span>
        </div>
        <p className="text-sm text-[#E5E5E5] leading-relaxed">
          If <b className="text-white">{email}</b> is on file, we&apos;ve sent a fresh invitation link.
          Check your inbox (and spam folder). Links expire after 30 days — click the newest one.
        </p>
        <Link
          to="/login"
          className="mt-4 inline-block text-xs uppercase tracking-widest underline text-white"
          data-testid="invite-resend-back-login"
        >
          Back to sign in
        </Link>
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="text-left space-y-3" data-testid="invite-resend-form">
      <div className="border border-white/10 rounded-lg p-4 bg-[#121212]">
        <div className="flex items-center gap-2 mb-3 text-[#A78BFA]">
          <RefreshCw size={14} />
          <span className="overline text-[10px]">// request a fresh invitation</span>
        </div>
        <p className="text-xs text-[#A1A1AA] mb-3 leading-relaxed">
          Enter the email address your coach used to invite you and we&apos;ll send a new link right away.
        </p>
        <label className="block mb-3">
          <span className="text-[10px] uppercase tracking-widest text-[#71717A] mb-1 flex items-center gap-1.5">
            <Mail size={10} /> Email
          </span>
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            data-testid="invite-resend-email"
            autoComplete="email"
            inputMode="email"
            className="w-full bg-transparent border-b border-white/20 focus:border-[#A78BFA] outline-none py-2 text-base"
          />
        </label>
        {err && (
          <div className="text-[#FF3B30] text-xs mb-2" data-testid="invite-resend-error">
            {err}
          </div>
        )}
        <button
          type="submit"
          disabled={busy}
          data-testid="invite-resend-submit"
          className="w-full py-2.5 bg-[#7C3AED] hover:bg-[#6D28D9] disabled:opacity-50 text-white font-heading text-xs uppercase tracking-[0.25em] flex items-center justify-center gap-2 rounded"
        >
          {busy ? <Loader2 size={13} className="animate-spin" /> : <Send size={13} />}
          {busy ? "Sending…" : "Send New Invitation"}
        </button>
      </div>
      <Link
        to="/login"
        className="inline-block text-[11px] text-[#71717A] hover:text-white underline"
        data-testid="invite-error-login-link"
      >
        Back to sign in
      </Link>
    </form>
  );
}

function Field({ label, icon: Icon, children }) {
  return (
    <label className="block">
      <span className="text-[10px] uppercase tracking-widest text-[#71717A] mb-1 flex items-center gap-1.5">
        {Icon && <Icon size={10} />} {label}
      </span>
      {children}
    </label>
  );
}
